/**
 * User Certificate Info Component
 *
 * Displays user's e-signature certificate status and details
 * Can be used in Users table as a column or standalone
 */

import React, { useState, useEffect } from 'react';
import { Tag, Tooltip, Button, Modal, Descriptions, Space } from 'antd';
import {
  SafetyCertificateOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
  EyeOutlined
} from '@ant-design/icons';
import Axios from 'axios';
import moment from 'moment';

const UserCertificateInfo = ({ userId, username, refreshTrigger }) => {
  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  // Format fingerprint in standard X.509 hex format (XX:XX:XX:XX...)
  const formatFingerprint = (fingerprint) => {
    if (!fingerprint) return 'N/A';

    // Remove any existing separators
    const cleaned = fingerprint.replace(/[:\s-]/g, '');

    // Split into pairs and join with colons
    const pairs = cleaned.match(/.{1,2}/g) || [];
    return pairs.join(':').toUpperCase();
  };

  useEffect(() => {
    if (userId) {
      loadCertificate();
    }
  }, [userId, refreshTrigger]);

  const loadCertificate = async () => {
    try {
      setLoading(true);

      // Skip if no userId provided
      if (!userId) {
        setCertificate(null);
        return;
      }

      // Pass employee_id as query parameter to fetch specific user's certificates
      const response = await Axios.get('/get_user_certificates', {
        params: {
          employee_id: userId
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('Token')}`
        }
      });

      if (response.data.status === 'success') {
        const certs = response.data.data || [];
        // Get the first valid certificate
        const validCert = certs.find(c => c.is_valid);
        setCertificate(validCert || certs[0] || null);
      } else {
        // API returned error - set no certificate
        setCertificate(null);
      }
    } catch (error) {
      console.error('Error loading certificate:', error);
      // On error, set no certificate
      setCertificate(null);
    } finally {
      setLoading(false);
    }
  };

  const getCertificateStatus = () => {
    if (!certificate) {
      return {
        status: 'none',
        color: 'default',
        text: 'No Certificate',
        icon: <CloseCircleOutlined />
      };
    }

    if (!certificate.is_valid) {
      return {
        status: 'invalid',
        color: 'error',
        text: 'Invalid',
        icon: <CloseCircleOutlined />
      };
    }

    const expiryDate = moment(certificate.expiry_date);
    const daysUntilExpiry = expiryDate.diff(moment(), 'days');

    if (daysUntilExpiry < 0) {
      return {
        status: 'expired',
        color: 'error',
        text: 'Expired',
        icon: <CloseCircleOutlined />
      };
    }

    if (daysUntilExpiry <= 30) {
      return {
        status: 'expiring',
        color: 'warning',
        text: `Expires in ${daysUntilExpiry} days`,
        icon: <WarningOutlined />
      };
    }

    return {
      status: 'valid',
      color: 'success',
      text: 'Valid',
      icon: <CheckCircleOutlined />
    };
  };

  const status = getCertificateStatus();

  // Show loading state
  if (loading) {
    return (
      <Tag color="default" icon={<SafetyCertificateOutlined spin />}>
        Loading...
      </Tag>
    );
  }

  return (
    <Space>
      <Tooltip title={certificate ? 'View certificate details' : 'No certificate uploaded'}>
        <Tag
          color={status.color}
          icon={status.icon}
          style={{ cursor: certificate ? 'pointer' : 'default' }}
          onClick={() => certificate && setShowDetails(true)}
        >
          {status.text}
        </Tag>
      </Tooltip>

      {certificate && (
        <Tooltip title="View details">
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => setShowDetails(true)}
          />
        </Tooltip>
      )}

      <Modal
        title={
          <Space>
            <SafetyCertificateOutlined style={{ color: '#1890ff' }} />
            X.509 Digital Certificate
          </Space>
        }
        visible={showDetails}
        onCancel={() => setShowDetails(false)}
        footer={[
          <Button key="close" onClick={() => setShowDetails(false)}>
            Close
          </Button>
        ]}
        width={700}
      >
        {certificate ? (
          <div style={{ fontFamily: 'monospace', fontSize: '12px' }}>
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label={<strong>Subject</strong>}>
                <div>CN={certificate.common_name}</div>
                <div>O={certificate.organization}</div>
              </Descriptions.Item>

              <Descriptions.Item label={<strong>Issuer</strong>}>
                {certificate.issuer || 'Self-Signed'}
              </Descriptions.Item>

              <Descriptions.Item label={<strong>Serial Number</strong>}>
                {certificate.serial_number || 'N/A'}
              </Descriptions.Item>

              <Descriptions.Item label={<strong>Validity Period</strong>}>
                <div><strong>Not Before:</strong> {moment(certificate.issued_date).format('DD-MMM-YYYY HH:mm:ss')}</div>
                <div><strong>Not After:</strong> {moment(certificate.expiry_date).format('DD-MMM-YYYY HH:mm:ss')}</div>
                <div style={{ marginTop: 8 }}>
                  <Tag color={status.color} icon={status.icon}>
                    {status.text}
                  </Tag>
                </div>
              </Descriptions.Item>

              <Descriptions.Item label={<strong>Certificate Status</strong>}>
                <Tag color={certificate.is_valid ? 'success' : 'error'} icon={certificate.is_valid ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
                  {certificate.is_valid ? 'VALID' : 'INVALID'}
                </Tag>
              </Descriptions.Item>

              <Descriptions.Item label={<strong>SHA-256 Fingerprint</strong>}>
                <div style={{
                  wordBreak: 'break-all',
                  fontFamily: 'monospace',
                  fontSize: '11px',
                  lineHeight: '1.6',
                  backgroundColor: '#f5f5f5',
                  padding: '8px',
                  borderRadius: '4px'
                }}>
                  {certificate.fingerprint ? formatFingerprint(certificate.fingerprint) : 'N/A'}
                </div>
              </Descriptions.Item>

              <Descriptions.Item label={<strong>Certificate Holder</strong>}>
                {username}
              </Descriptions.Item>
            </Descriptions>

            <div style={{
              marginTop: 16,
              padding: 10,
              backgroundColor: '#e6f7ff',
              border: '1px solid #91d5ff',
              borderRadius: 4,
              fontSize: 11
            }}>
              <SafetyCertificateOutlined style={{ marginRight: 8, color: '#1890ff' }} />
              <strong>21 CFR Part 11 Compliant:</strong> This certificate is used for electronic signature authentication in compliance with FDA regulations.
            </div>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
            <SafetyCertificateOutlined style={{ fontSize: 48, marginBottom: 16 }} />
            <p>No certificate has been uploaded yet.</p>
            <p>Upload a certificate (.pem, .crt, .cer) in User Management to enable certificate-based e-signatures for this user.</p>
          </div>
        )}
      </Modal>
    </Space>
  );
};

export default UserCertificateInfo;
